/**
 * CheckoutPage — Route /checkout (Paket B, premium-checkout-redesign)
 *
 * Design-Referenz: design/checkout.html. Summary links (Plan, Laufzeit,
 * Preisaufschlüsselung, Trust-Bullets), Zahlung rechts (Tabs Karte/HNT).
 *
 * Restwert-Anrechnung: bei bestehendem aktivem Abo wird der Restwert über
 * usePreviewUpgrade(plan.id, term) geladen (BE-Konvention, siehe planMath.ts).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Wallet, Radio } from 'lucide-react'
import { useCryptoPrices } from '../../hooks/useCryptoPrices'
import { useSubscription } from '../subscription/useSubscription'
import { useSubscriptionPlans, displayPrice } from '../subscription/useSubscriptionPlans'
import { type DurationMonths, isExpiredSubscription } from '../subscription/subscriptionUtils'
import { usePreviewUpgrade } from '../premium/usePreviewUpgrade'
import { findCatalogPlan } from '../premium/legacyPlanMap'
import { planDisplayName } from '../premium/planDisplayName'
import { dueToday } from '../premium/planMath'
import { CheckoutSummary } from './CheckoutSummary'
import { CardPaymentPane } from './CardPaymentPane'
import { HntPaymentPane } from './HntPaymentPane'
import './checkout.css'

type PayTab = 'card' | 'hnt'

type CheckoutPageProps = {
  initialPlanId?: string
  initialTerm?: DurationMonths
  onTermChange: (term: DurationMonths) => void
  onCheckoutSuccess: (planId: string, term: DurationMonths, method: PayTab) => void
}

export function CheckoutPage({ initialPlanId, initialTerm, onTermChange, onCheckoutSuccess }: CheckoutPageProps) {
  const { t } = useTranslation('checkout')
  const { currency } = useCryptoPrices()
  const { plans, isLoading: plansLoading } = useSubscriptionPlans(currency)
  const { subscription, subscribe, changePlan, requestCryptoQuote, refetch: refetchSubscription } = useSubscription()
  const { previewUpgrade, isPending: previewPending } = usePreviewUpgrade()

  const [term, setTerm] = useState<DurationMonths>(initialTerm ?? 1)
  const [tab, setTab] = useState<PayTab>('card')
  const [credit, setCredit] = useState(0)
  const [previewMode, setPreviewMode] = useState<'stripe_swap' | 'conversion' | 'checkout'>('checkout')
  const [conversionExpiresAt, setConversionExpiresAt] = useState<string | null>(null)
  const [daysRemaining, setDaysRemaining] = useState<number | undefined>(undefined)

  const plan = findCatalogPlan(plans, initialPlanId)
  const hasActiveSubscription = Boolean(subscription) && !isExpiredSubscription(subscription)
  const currentPlan = hasActiveSubscription ? findCatalogPlan(plans, subscription?.plan_id) : null
  const currentPlanName = currentPlan?.name ?? planDisplayName(subscription?.plan_id) ?? null

  useEffect(() => {
    // credit wird nur verwendet, wenn hasActiveSubscription true ist (siehe
    // amountDue/CheckoutSummary) — ein Reset auf 0 hier ist daher unnötig
    // und würde synchron im Effekt-Body setState aufrufen (verboten).
    if (!plan || !hasActiveSubscription) return
    let active = true
    void previewUpgrade(plan.id, term).then((res) => {
      if (!active || !res.ok) return
      // BUG-Fix: unused_credit ist bei Upgrades immer 0.0 (Feld ist für den
      // Downgrade-Pfad gedacht) — der Restwert des laufenden Plans kommt aus
      // proration_credit.
      setCredit(res.proration_credit ?? 0)
      setPreviewMode(res.mode ?? 'checkout')
      setConversionExpiresAt(res.conversion_expires_at ?? null)
      setDaysRemaining(res.days_remaining)
    })
    return () => { active = false }
  }, [plan, term, hasActiveSubscription, previewUpgrade])

  function handleTermChange(next: DurationMonths) {
    setTerm(next)
    onTermChange(next)
  }

  function handleSuccess() {
    if (plan) onCheckoutSuccess(plan.id, term, tab)
  }

  if (plansLoading) {
    return <div aria-busy="true" data-testid="checkout-loading" />
  }

  if (!plan) {
    return (
      <div style={{ maxWidth: 640, margin: '40px auto', textAlign: 'center' }} data-testid="checkout-unknown-plan">
        <p className="muted">{t('unknownPlan.text')}</p>
        <Link to="/plans" className="btn btn-primary">{t('unknownPlan.link')}</Link>
      </div>
    )
  }

  return (
    <div data-testid="checkout-page">
      <div style={{ maxWidth: 1040, margin: '0 auto 18px' }}>
        <Link to="/plans" className="btn btn-ghost btn-sm" style={{ marginBottom: 10 }}>{t('back')}</Link>
        <h1>{hasActiveSubscription ? t('title', { plan: plan.name }) : t('titleNoSub', { plan: plan.name })}</h1>
        <p className="muted" style={{ margin: '6px 0 0' }}>{t('subtitle')}</p>
      </div>

      <div className="co-wrap">
        <CheckoutSummary
          plan={plan}
          term={term}
          onTermChange={handleTermChange}
          currentPlanName={currentPlanName}
          credit={credit}
          hasActiveSubscription={hasActiveSubscription}
          payTab={tab}
          mode={previewMode}
          conversionExpiresAt={conversionExpiresAt}
          daysRemaining={daysRemaining}
        />

        <div className="card card-pad-lg">
          <div className="paytabs" role="tablist" aria-label={t('payment.tabsAriaLabel')}>
            <div
              className={`paytab ${tab === 'card' ? 'is-active' : ''}`}
              role="tab"
              tabIndex={0}
              aria-selected={tab === 'card'}
              onClick={() => setTab('card')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('card') } }}
              data-testid="tab-card"
            >
              <span className="tico"><Wallet size={16} /></span>
              <div><b>{t('payment.card.title')}</b><span>{t('payment.card.subtitle')}</span></div>
            </div>
            <div
              className={`paytab ${tab === 'hnt' ? 'is-active' : ''}`}
              role="tab"
              tabIndex={0}
              aria-selected={tab === 'hnt'}
              onClick={() => setTab('hnt')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('hnt') } }}
              data-testid="tab-hnt"
            >
              <span className="tico"><Radio size={16} /></span>
              <div><b>{t('payment.hnt.title')}</b><span>{t('payment.hnt.subtitle')}</span></div>
            </div>
          </div>

          {tab === 'card' ? (
            <CardPaymentPane
              plan={plan}
              duration={term}
              amountDue={dueToday(displayPrice(plan, term), hasActiveSubscription ? credit : 0)}
              subscription={subscription}
              subscribe={subscribe}
              changePlan={changePlan}
              refetchSubscription={refetchSubscription}
              onSuccess={handleSuccess}
              mode={hasActiveSubscription ? previewMode : 'checkout'}
              conversionExpiresAt={conversionExpiresAt}
              daysRemaining={daysRemaining}
              previewLoading={hasActiveSubscription && previewPending}
            />
          ) : (
            <HntPaymentPane
              plan={plan}
              duration={term}
              requestCryptoQuote={requestCryptoQuote}
              refetchSubscription={refetchSubscription}
              onSuccess={handleSuccess}
            />
          )}
        </div>
      </div>
    </div>
  )
}
